import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowClockwise, FloppyDisk } from "@phosphor-icons/react";
import { getPromptTemplates, updatePromptTemplates, resetPromptTemplates } from "@/lib/api";

const PLACEHOLDERS = {
  article: [
    "{title}",
    "{channel_name}",
    "{category}",
    "{duration}",
    "{language}",
    "{word_count}",
  ],
  regen: [
    "{title}",
    "{channel_name}",
    "{category}",
    "{language}",
    "{other_parts_summary}",
    "{part_heading}",
    "{part_body}",
  ],
  scenes: [
    "{title}",
    "{channel_name}",
    "{category}",
    "{language}",
    "{language_en}",
    "{full_article}",
  ],
};

const PlaceholderList = ({ items }) => (
  <div className="flex flex-wrap gap-1.5 mt-2">
    {items.map((p) => (
      <code
        key={p}
        className="font-mono text-[10px] px-1.5 py-0.5 border border-border text-muted-foreground bg-[#0F0F0F]"
      >
        {p}
      </code>
    ))}
  </div>
);

export const PromptSettingsDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tpl, setTpl] = useState({
    article_prompt: "",
    regenerate_part_prompt: "",
    scenes_prompt: "",
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getPromptTemplates()
      .then(setTpl)
      .catch(() => toast.error("Prompt şablonları yüklenemedi"))
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updatePromptTemplates(tpl);
      setTpl(updated);
      toast.success("Prompt şablonları kaydedildi");
      onOpenChange(false);
    } catch {
      toast.error("Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Tüm prompt şablonlarını varsayılana döndürmek istiyor musunuz?"))
      return;
    setSaving(true);
    try {
      const fresh = await resetPromptTemplates();
      setTpl(fresh);
      toast.success("Varsayılan şablonlar geri yüklendi");
    } catch {
      toast.error("Sıfırlama başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="prompt-settings-dialog"
        className="bg-[#0F0F0F] border border-border rounded-none max-w-3xl p-0 gap-0 shadow-none"
      >
        <DialogHeader className="p-6 border-b border-border">
          <span className="label-mono text-primary mb-1">// global_prompt_templates</span>
          <DialogTitle className="heading-font font-black text-2xl tracking-tight text-white">
            Prompt Ayarları
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1">
            Tüm kanallar için ortak AI şablonları. Placeholder&apos;lar otomatik dolar.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6">
          <Tabs defaultValue="article">
            <TabsList className="bg-[#0A0A0A] rounded-none border border-border p-0 h-auto">
              <TabsTrigger
                data-testid="tab-article"
                value="article"
                className="rounded-none label-mono px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                1 · Makale Üret
              </TabsTrigger>
              <TabsTrigger
                data-testid="tab-regen"
                value="regen"
                className="rounded-none label-mono px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                2 · Partı Yeniden Üret
              </TabsTrigger>
              <TabsTrigger
                data-testid="tab-scenes"
                value="scenes"
                className="rounded-none label-mono px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                3 · Görsel Bölümleme
              </TabsTrigger>
            </TabsList>

            <TabsContent value="article" className="mt-4">
              <label className="label-mono text-muted-foreground">Şablon</label>
              <Textarea
                data-testid="prompt-article-textarea"
                value={tpl.article_prompt}
                onChange={(e) => setTpl({ ...tpl, article_prompt: e.target.value })}
                disabled={loading}
                className="mt-2 min-h-[320px] bg-background border-border rounded-none font-mono text-[12.5px] leading-relaxed focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0"
              />
              <PlaceholderList items={PLACEHOLDERS.article} />
            </TabsContent>

            <TabsContent value="regen" className="mt-4">
              <label className="label-mono text-muted-foreground">Şablon</label>
              <Textarea
                data-testid="prompt-regen-textarea"
                value={tpl.regenerate_part_prompt}
                onChange={(e) =>
                  setTpl({ ...tpl, regenerate_part_prompt: e.target.value })
                }
                disabled={loading}
                className="mt-2 min-h-[320px] bg-background border-border rounded-none font-mono text-[12.5px] leading-relaxed focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0"
              />
              <PlaceholderList items={PLACEHOLDERS.regen} />
            </TabsContent>

            <TabsContent value="scenes" className="mt-4">
              <label className="label-mono text-muted-foreground">Şablon</label>
              <Textarea
                data-testid="prompt-scenes-textarea"
                value={tpl.scenes_prompt}
                onChange={(e) => setTpl({ ...tpl, scenes_prompt: e.target.value })}
                disabled={loading}
                className="mt-2 min-h-[320px] bg-background border-border rounded-none font-mono text-[12.5px] leading-relaxed focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0"
              />
              <PlaceholderList items={PLACEHOLDERS.scenes} />
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className="p-4 border-t border-border gap-2 flex-row justify-between">
          <Button
            data-testid="prompt-reset-btn"
            variant="ghost"
            onClick={reset}
            disabled={saving}
            className="rounded-none h-10 text-muted-foreground hover:text-primary hover:bg-transparent"
          >
            <ArrowClockwise size={14} className="mr-2" />
            Varsayılana Döndür
          </Button>
          <div className="flex gap-2">
            <Button
              data-testid="prompt-cancel-btn"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="rounded-none h-10 text-muted-foreground hover:text-white hover:bg-[#1A1A1A]"
            >
              Vazgeç
            </Button>
            <Button
              data-testid="prompt-save-btn"
              onClick={save}
              disabled={saving || loading}
              className="rounded-none h-10 bg-primary hover:bg-[#D63028] text-white font-bold uppercase tracking-wider text-xs"
            >
              <FloppyDisk size={14} weight="bold" className="mr-2" />
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromptSettingsDialog;
